import { defaultSerializeQueryArgs } from "@reduxjs/toolkit/query";

/**
 * objectURLResponseHandler is the `responseHandler` of a query returning a
 * file. The file is returned as an object URL, to be rendered e.g. in an
 * iframe or an img. Error responses are passed through.
 *
 * Spread {@link revokeObjectURLOptions} into the endpoint, so the object
 * URLs are revoked again.
 */
export async function objectURLResponseHandler(response: Response) {
  if (response.status !== 200) {
    return response.json();
  }
  return {
    objectURL: window.URL.createObjectURL(await response.blob()),
  };
}

/**
 * revokeObjectURLOptions returns the options spread into every query
 * endpoint whose result holds an `objectURL`, e.g. one created by
 * {@link objectURLResponseHandler}. The object URL of every entry is revoked
 * once it is replaced by a refetch or the entry is removed, so the blobs do
 * not pile up in memory. The cache entry owns the URL, not a component: a
 * component unmounting or downloading the file must not revoke a URL the
 * cache still hands out.
 *
 * Call it once per endpoint: it tracks the current object URL of each of
 * the endpoint's cache entries, keyed by the query arg.
 *
 * @example
 * pdfPreview: build.query<{ objectURL: string }, PDFRequest>({
 *   ...revokeObjectURLOptions(),
 *   query: (request) => ({ ..., responseHandler: objectURLResponseHandler }),
 * }),
 */
export function revokeObjectURLOptions() {
  // The object URL each cache entry currently holds. Tracked here because
  // the entry is already gone from the store once `cacheEntryRemoved`
  // resolves, so it can no longer be read from there.
  const objectURLs = new Map<string, string>();

  return {
    async onQueryStarted(
      arg: unknown,
      {
        queryFulfilled,
      }: { queryFulfilled: Promise<{ data: { objectURL: string } }> },
    ) {
      let objectURL: string;
      try {
        ({
          data: { objectURL },
        } = await queryFulfilled);
      } catch {
        // A failed (re)fetch keeps the previous URL in the entry.
        return;
      }
      const key = cacheKey(arg);
      const previous = objectURLs.get(key);
      objectURLs.set(key, objectURL);
      if (previous && previous !== objectURL) {
        window.URL.revokeObjectURL(previous);
      }
    },
    async onCacheEntryAdded(
      arg: unknown,
      { cacheEntryRemoved }: { cacheEntryRemoved: Promise<void> },
    ) {
      await cacheEntryRemoved;
      const key = cacheKey(arg);
      const last = objectURLs.get(key);
      objectURLs.delete(key);
      if (last) {
        window.URL.revokeObjectURL(last);
      }
    },
  };
}

// cacheKey serializes the query arg the way RTK Query keys its cache entries,
// independent of the order of the arg's keys. The endpoint is left out: the
// URLs are tracked per endpoint already.
function cacheKey(arg: unknown): string {
  return defaultSerializeQueryArgs({
    queryArgs: arg,
    endpointName: "",
    // Not read by the default serialization.
    endpointDefinition: undefined as unknown as Parameters<
      typeof defaultSerializeQueryArgs
    >[0]["endpointDefinition"],
  });
}
